import React from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Code,
  Badge,
  Divider,
  Alert,
  AlertIcon,
  Image,
} from "@chakra-ui/react";

// Architecture diagram - rendered from Excalidraw
const ArchitectureDiagram: React.FC = () => {
  return (
    <Box>
      <Image
        src="/excalidraw/local-pvm-debug-architecture.png"
        alt="Local PVM Debug Architecture - shows local sync node connected to production validator via P2P, local PVM connected to sync node via HTTP"
        borderRadius="lg"
        borderWidth="1px"
        borderColor="gray.200"
        w="100%"
      />
    </Box>
  );
};

// Copyable terminal block component
const Terminal: React.FC<{ children: React.ReactNode; title?: string }> = ({ children, title }) => (
  <Box>
    {title && (
      <HStack bg="gray.700" px={4} py={2} borderTopRadius="md" spacing={2}>
        <Box w="12px" h="12px" borderRadius="full" bg="red.400" />
        <Box w="12px" h="12px" borderRadius="full" bg="yellow.400" />
        <Box w="12px" h="12px" borderRadius="full" bg="green.400" />
        <Text color="gray.300" fontSize="xs" ml={2}>{title}</Text>
      </HStack>
    )}
    <Box
      bg="gray.900"
      color="green.300"
      p={4}
      borderRadius={title ? "0 0 8px 8px" : "md"}
      fontFamily="mono"
      fontSize="sm"
      overflowX="auto"
      sx={{ "& ::selection": { bg: "blue.600" } }}
    >
      {children}
    </Box>
  </Box>
);

const Cmd: React.FC<{ children: string }> = ({ children }) => (
  <Text>{children}</Text>
);

const Comment: React.FC<{ children: string }> = ({ children }) => (
  <Text color="gray.500">{children}</Text>
);

const Highlight: React.FC<{ children: string }> = ({ children }) => (
  <Text color="yellow.300">{children}</Text>
);

const LocalPvmDebugSetup: React.FC = () => {
  return (
    <Container maxW="container.lg" py={8}>
      <VStack spacing={8} align="stretch">
        {/* Header */}
        <Box>
          <Heading size="xl" mb={2}>Local PVM Debug Setup</Heading>
          <Text color="gray.600">
            Run a local sync node + PVM for debugging poker game logic with VSCode breakpoints.
          </Text>
        </Box>

        {/* Architecture Diagram */}
        <Box>
          <Heading size="md" mb={4}>How It Works</Heading>
          <ArchitectureDiagram />
        </Box>

        <Divider />

        {/* Prerequisites */}
        <Box>
          <Heading size="md" mb={3}>Prerequisites</Heading>
          <VStack align="stretch" spacing={2}>
            <HStack><Badge colorScheme="green">Required</Badge><Text>Go 1.24.7+</Text></HStack>
            <HStack><Badge colorScheme="green">Required</Badge><Text>Node.js 20.18+ and yarn</Text></HStack>
            <HStack><Badge colorScheme="blue">Optional</Badge><Text>VSCode with TypeScript debugger (for breakpoints)</Text></HStack>
          </VStack>
        </Box>

        <Divider />

        {/* PHASE 1 */}
        <Box>
          <HStack mb={3}>
            <Badge colorScheme="blue" fontSize="lg" px={4} py={1}>Phase 1</Badge>
            <Heading size="md">Initial Sync (one-time setup)</Heading>
          </HStack>
          <Text mb={3} color="gray.600">
            First we sync the blockchain from production. This only needs to be done once.
          </Text>

          <Terminal title="Terminal 1 - Sync the chain">
            <Comment># 1. Remove any old sync data</Comment>
            <Cmd>rm -rf ~/.pokerchain-dev</Cmd>
            <Text mt={3} />
            <Comment># 2. Start state sync (downloads recent snapshot, much faster than replaying all blocks)</Comment>
            <Cmd>cd pokerchain</Cmd>
            <Cmd>./run-dev-node.sh --statesync --reset</Cmd>
            <Text mt={2} />
            <Highlight># When prompted:</Highlight>
            <Highlight>#   "Execution Layer" -&gt; Choose 1 (Chain only)</Highlight>
            <Highlight>#   "Which PVM"       -&gt; Choose 1 (Production PVM)</Highlight>
            <Text mt={2} />
            <Comment># 3. Wait until you see blocks being committed:</Comment>
            <Comment>#    "committed state height=70xxx"</Comment>
            <Comment>#    Then press Ctrl+C to stop</Comment>
          </Terminal>
        </Box>

        <Divider />

        {/* PHASE 2 */}
        <Box>
          <HStack mb={3}>
            <Badge colorScheme="purple" fontSize="lg" px={4} py={1}>Phase 2</Badge>
            <Heading size="md">Start Local PVM</Heading>
          </HStack>
          <Text mb={3} color="gray.600">
            Start the PVM game engine locally so you can set breakpoints.
          </Text>

          <Terminal title="Terminal 1 - PVM (leave this running)">
            <Cmd>cd poker-vm/pvm/ts</Cmd>
            <Cmd>yarn install</Cmd>
            <Cmd>yarn dev</Cmd>
            <Text mt={2} />
            <Comment># Should print: PVM RPC Server v0.1.1 running on http://localhost:8545</Comment>
            <Comment># Or press F5 in VSCode to start with debugger attached</Comment>
          </Terminal>
        </Box>

        <Divider />

        {/* PHASE 3 */}
        <Box>
          <HStack mb={3}>
            <Badge colorScheme="orange" fontSize="lg" px={4} py={1}>Phase 3</Badge>
            <Heading size="md">Point Chain to Local PVM</Heading>
          </HStack>
          <Text mb={3} color="gray.600">
            Tell the chain to use your local PVM instead of production.
          </Text>

          <Alert status="warning" mb={4} borderRadius="md">
            <AlertIcon />
            <Box>
              <Text fontWeight="bold">Key file to edit:</Text>
              <Code colorScheme="yellow" fontSize="sm">~/.pokerchain-dev/config/app.toml</Code>
            </Box>
          </Alert>

          <Terminal title="Terminal 2 - Update config and start chain">
            <Comment># 1. Change pvm_url from production to local</Comment>
            <Cmd>{"sed -i '' 's|pvm_url = \"https://pvm.block52.xyz\"|pvm_url = \"http://localhost:8545\"|' ~/.pokerchain-dev/config/app.toml"}</Cmd>
            <Text mt={2} />
            <Comment># 2. Verify it changed (should show localhost:8545)</Comment>
            <Cmd>grep pvm_url ~/.pokerchain-dev/config/app.toml</Cmd>
            <Text mt={2} />
            <Comment># 3. Start the chain</Comment>
            <Cmd>cd pokerchain</Cmd>
            <Cmd>./build/pokerchaind start --home ~/.pokerchain-dev</Cmd>
          </Terminal>

          <Text fontSize="sm" color="gray.500" mt={2}>
            If the sed command doesn't work, just open <Code fontSize="xs">~/.pokerchain-dev/config/app.toml</Code> in
            your editor, find the <Code fontSize="xs">[pvm]</Code> section near the bottom, and change the pvm_url line.
          </Text>
        </Box>

        <Divider />

        {/* PHASE 4 */}
        <Box>
          <HStack mb={3}>
            <Badge colorScheme="green" fontSize="lg" px={4} py={1}>Phase 4</Badge>
            <Heading size="md">Verify</Heading>
          </HStack>

          <Terminal title="Terminal 3 - Check status">
            <Comment># Check the chain is running and syncing</Comment>
            <Cmd>{"curl -s http://127.0.0.1:26657/status | jq '.result.sync_info'"}</Cmd>
            <Text mt={2} />
            <Comment># Check PVM is responding</Comment>
            <Cmd>curl -s http://localhost:8545</Cmd>
            <Comment># Should print: PVM RPC Server v0.1.1</Comment>
          </Terminal>
        </Box>

        <Divider />

        {/* Troubleshooting */}
        <Box>
          <Heading size="md" mb={3}>Troubleshooting</Heading>
          <VStack align="stretch" spacing={3}>
            <Box p={4} borderWidth="1px" borderRadius="md" borderColor="orange.200" bg="orange.50">
              <Text fontWeight="bold" color="orange.700">Chain panics on startup</Text>
              <Text fontSize="sm" color="orange.600" mt={1}>
                PVM must be running before the chain starts. Start PVM first (Phase 2), then the chain (Phase 3).
                Or skip the health check: <Code fontSize="sm">PVM_SKIP_HEALTH_CHECK=1 ./build/pokerchaind start --home ~/.pokerchain-dev</Code>
              </Text>
            </Box>

            <Box p={4} borderWidth="1px" borderRadius="md" borderColor="orange.200" bg="orange.50">
              <Text fontWeight="bold" color="orange.700">AppHash mismatch at block 151</Text>
              <Text fontSize="sm" color="orange.600" mt={1}>
                Use <Code fontSize="sm">--statesync</Code> flag in Phase 1. This downloads a snapshot instead of replaying from genesis.
              </Text>
            </Box>

            <Box p={4} borderWidth="1px" borderRadius="md" borderColor="orange.200" bg="orange.50">
              <Text fontWeight="bold" color="orange.700">sed command doesn't change anything</Text>
              <Text fontSize="sm" color="orange.600" mt={1}>
                Open <Code fontSize="sm">~/.pokerchain-dev/config/app.toml</Code> manually. Scroll to the <Code fontSize="sm">[pvm]</Code> section
                at the bottom and change <Code fontSize="sm">pvm_url</Code> to <Code fontSize="sm">"http://localhost:8545"</Code>.
              </Text>
            </Box>

            <Box p={4} borderWidth="1px" borderRadius="md" borderColor="orange.200" bg="orange.50">
              <Text fontWeight="bold" color="orange.700">./build/pokerchaind not found</Text>
              <Text fontSize="sm" color="orange.600" mt={1}>
                Make sure you <Code fontSize="sm">cd pokerchain</Code> first (the repo directory, not ~/.pokerchain-dev).
                Or use <Code fontSize="sm">pokerchaind</Code> directly if it's in your PATH.
              </Text>
            </Box>

            <Box p={4} borderWidth="1px" borderRadius="md" borderColor="orange.200" bg="orange.50">
              <Text fontWeight="bold" color="orange.700">Cannot find module '@block52/poker-vm-sdk'</Text>
              <Text fontSize="sm" color="orange.600" mt={1}>
                Run <Code fontSize="sm">yarn install</Code> in <Code fontSize="sm">poker-vm/pvm/ts/</Code>. The SDK is published on npm
                at <Code fontSize="sm">@block52/poker-vm-sdk</Code>. If you see a yarn link issue,
                run <Code fontSize="sm">yarn unlink "@block52/poker-vm-sdk" && yarn install --force</Code>.
              </Text>
            </Box>
          </VStack>
        </Box>

        <Divider />

        {/* Key Files Reference */}
        <Box>
          <Heading size="md" mb={3}>Key Files</Heading>
          <Box overflowX="auto">
            <Box as="table" width="100%" fontSize="sm">
              <Box as="thead">
                <Box as="tr" bg="gray.100">
                  <Box as="th" p={2} textAlign="left">File</Box>
                  <Box as="th" p={2} textAlign="left">What</Box>
                </Box>
              </Box>
              <Box as="tbody">
                <Box as="tr" borderBottomWidth="1px" bg="yellow.50">
                  <Box as="td" p={2}><Code fontSize="xs" colorScheme="yellow">~/.pokerchain-dev/config/app.toml</Code></Box>
                  <Box as="td" p={2}>Change <Code fontSize="xs">pvm_url</Code> here to switch local/production PVM</Box>
                </Box>
                <Box as="tr" borderBottomWidth="1px">
                  <Box as="td" p={2}><Code fontSize="xs">pokerchain/.env</Code></Box>
                  <Box as="td" p={2}>PVM_URL for initial sync (read by run-dev-node.sh)</Box>
                </Box>
                <Box as="tr" borderBottomWidth="1px">
                  <Box as="td" p={2}><Code fontSize="xs">pokerchain/run-dev-node.sh</Code></Box>
                  <Box as="td" p={2}>Automated setup script (builds binary, configures node, starts chain)</Box>
                </Box>
                <Box as="tr" borderBottomWidth="1px">
                  <Box as="td" p={2}><Code fontSize="xs">poker-vm/pvm/ts/</Code></Box>
                  <Box as="td" p={2}>PVM source code - set breakpoints here</Box>
                </Box>
                <Box as="tr" borderBottomWidth="1px">
                  <Box as="td" p={2}><Code fontSize="xs">pokerchain/build/pokerchaind</Code></Box>
                  <Box as="td" p={2}>Chain binary (run from pokerchain/ directory)</Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Footer */}
        <Box textAlign="center" py={4}>
          <Text fontSize="sm" color="gray.400">
            Block52 Developer Cheatsheet
          </Text>
        </Box>
      </VStack>
    </Container>
  );
};

export default LocalPvmDebugSetup;
